import * as _ from 'lodash';
import { Injectable, inject } from '@angular/core';

import { FcpMessageService, FcpMessageSeverity } from 'fc-design-system';
import { LanguageService } from 'services';
import {
    BulkDownloadImagesInformation,
    BulkDownloadImagesStatistics,
    BulkDownloadOmissionReason,
    BulkSearchResultWithInformation
} from 'app/content/models/product.model';

export interface BulkDownloadToastSelection {
    images: boolean;
    video: boolean;
    csv: boolean;
}

export type BulkDownloadToastSearchBy = 'gtins' | 'articleNumbers';

export interface BulkDownloadStartedToastInput {
    searchBy: BulkDownloadToastSearchBy;
    selection: BulkDownloadToastSelection;
    downloadInformation: BulkDownloadImagesInformation;
    additionalData?: BulkDownloadImagesStatistics;
}

export const BULK_DOWNLOAD_NOT_STARTED_KEYS = {
    title: 'BULK_GTIN_SEARCH.DOWNLOAD_NOT_STARTED.TITLE',
    gtins: {
        detail: 'BULK_GTIN_SEARCH.DOWNLOAD_NOT_STARTED.GTINS.DETAIL'
    },
    articleNumbers: {
        detail: 'BULK_GTIN_SEARCH.DOWNLOAD_NOT_STARTED.ARTICLE_NUMBERS.DETAIL'
    }
} as const;

/** Too many identifiers — reuses existing bulk-search copy; summary matches not-started title. */
export const BULK_DOWNLOAD_TOO_MANY_KEYS = {
    title: 'BULK_GTIN_SEARCH.DOWNLOAD_NOT_STARTED.TITLE',
    gtins: {
        detail: 'BULK_GTIN_SEARCH.TOO_MANY_GTINS'
    },
    articleNumbers: {
        detail: 'BULK_SEARCH_LIMIT_EXCEED_ERROR'
    }
} as const;

export const BULK_DOWNLOAD_STARTED_KEYS = {
    title: 'BULK_GTIN_SEARCH.DOWNLOAD_STARTED.TITLE',
    mediaImages: 'BULK_GTIN_SEARCH.DOWNLOAD_STARTED.MEDIA_IMAGES',
    mediaVideos: 'BULK_GTIN_SEARCH.DOWNLOAD_STARTED.MEDIA_VIDEOS',
    mediaCsv: 'BULK_GTIN_SEARCH.DOWNLOAD_STARTED.MEDIA_CSV',
    mediaPair: 'BULK_GTIN_SEARCH.DOWNLOAD_STARTED.MEDIA_PAIR',
    mediaConjunction: 'BULK_GTIN_SEARCH.DOWNLOAD_STARTED.MEDIA_CONJUNCTION',
    gtins: {
        intro: 'BULK_GTIN_SEARCH.DOWNLOAD_STARTED.GTINS.INTRO',
        notFound: 'BULK_GTIN_SEARCH.DOWNLOAD_STARTED.GTINS.NOT_FOUND'
    },
    articleNumbers: {
        intro: 'BULK_GTIN_SEARCH.DOWNLOAD_STARTED.ARTICLE_NUMBERS.INTRO',
        notFound: 'BULK_GTIN_SEARCH.DOWNLOAD_STARTED.ARTICLE_NUMBERS.NOT_FOUND'
    },
    videoOmitted: 'BULK_GTIN_SEARCH.DOWNLOAD_STARTED.VIDEO_OMITTED',
    productsWithoutVideo: 'BULK_GTIN_SEARCH.DOWNLOAD_STARTED.PRODUCTS_WITHOUT_VIDEO',
    videoOmissionReasons: 'BULK_GTIN_SEARCH.DOWNLOAD_STARTED.VIDEO_OMISSION_REASONS'
} as const;

export const BULK_DOWNLOAD_INTERNAL_ERROR_KEYS = {
    title: 'BULK_GTIN_SEARCH.DOWNLOAD_INTERNAL_ERROR.TITLE',
    detail: 'BULK_GTIN_SEARCH.DOWNLOAD_INTERNAL_ERROR.DETAIL'
} as const;

export const BULK_DOWNLOAD_CSV_OMITTED_KEYS = {
    title: 'DOWNLOAD_CONTENT_MODAL.CSV_OMITTED.TITLE',
    detail: 'DOWNLOAD_CONTENT_MODAL.CSV_OMITTED.DETAIL'
} as const;

export const BULK_DOWNLOAD_CSV_SKIPPED_KEYS = {
    title: 'DOWNLOAD_CONTENT_MODAL.CSV_SKIPPED.TITLE',
    detail: 'DOWNLOAD_CONTENT_MODAL.CSV_SKIPPED.DETAIL'
} as const;

export const BULK_DOWNLOAD_NO_ASSETS_FOUND_KEYS = {
    title: 'BULK_GTIN_SEARCH.NO_ASSETS_FOUND.TITLE',
    mediaImages: 'BULK_GTIN_SEARCH.NO_ASSETS_FOUND.MEDIA_IMAGES',
    mediaVideos: 'BULK_GTIN_SEARCH.NO_ASSETS_FOUND.MEDIA_VIDEOS',
    mediaCsv: 'BULK_GTIN_SEARCH.NO_ASSETS_FOUND.MEDIA_CSV',
    mediaPair: 'BULK_GTIN_SEARCH.NO_ASSETS_FOUND.MEDIA_PAIR',
    mediaConjunction: 'BULK_GTIN_SEARCH.NO_ASSETS_FOUND.MEDIA_CONJUNCTION',
    omissionReasons: 'BULK_GTIN_SEARCH.NO_ASSETS_FOUND.OMISSION_REASONS',
    gtins: {
        detail: 'BULK_GTIN_SEARCH.NO_ASSETS_FOUND.GTINS.DETAIL'
    },
    articleNumbers: {
        detail: 'BULK_GTIN_SEARCH.NO_ASSETS_FOUND.ARTICLE_NUMBERS.DETAIL'
    }
} as const;

type MediaListTranslationKeys = {
    mediaImages: string;
    mediaVideos: string;
    mediaCsv: string;
    mediaPair: string;
    mediaConjunction: string;
};

@Injectable({ providedIn: 'root' })
export class BulkDownloadToastService {
    private readonly languageService = inject(LanguageService);
    private readonly messageService = inject(FcpMessageService);

    /**
     * Error toast when every requested GTIN / article number was not found (no main download token).
     */
    async showDownloadNotStarted(input: {
        searchBy: BulkDownloadToastSearchBy;
        downloadInformation: BulkDownloadImagesInformation;
    }): Promise<void> {
        const foundCount = Math.max(0, input.downloadInformation.requested - input.downloadInformation.notFound);
        const copyKeys = BULK_DOWNLOAD_NOT_STARTED_KEYS[input.searchBy];

        const [summary, detail] = await Promise.all([
            this.languageService.translate(BULK_DOWNLOAD_NOT_STARTED_KEYS.title),
            this.languageService.translate(copyKeys.detail, { count: foundCount })
        ]);

        this.messageService.add({
            severity: FcpMessageSeverity.Error,
            summary,
            detail,
            life: 8000,
            closable: true
        });
    }

    async showTooManyGtins(searchBy: BulkDownloadToastSearchBy): Promise<void> {
        const detailKey = BULK_DOWNLOAD_TOO_MANY_KEYS[searchBy].detail;

        const [summary, detail] = await Promise.all([
            this.languageService.translate(BULK_DOWNLOAD_TOO_MANY_KEYS.title),
            this.languageService.translate(detailKey)
        ]);

        this.messageService.add({
            severity: FcpMessageSeverity.Error,
            summary,
            detail,
            life: 8000,
            closable: true
        });
    }

    async showInternalError(): Promise<void> {
        const [summary, detail] = await Promise.all([
            this.languageService.translate(BULK_DOWNLOAD_INTERNAL_ERROR_KEYS.title),
            this.languageService.translate(BULK_DOWNLOAD_INTERNAL_ERROR_KEYS.detail)
        ]);

        this.messageService.add({
            severity: FcpMessageSeverity.Error,
            summary,
            detail,
            life: 8000,
            closable: true
        });
    }

    /**
     * Warning toast when the files are downloading but the requested CSV could not be generated.
     */
    async showCsvOmitted(): Promise<void> {
        const [summary, detail] = await Promise.all([
            this.languageService.translate(BULK_DOWNLOAD_CSV_OMITTED_KEYS.title),
            this.languageService.translate(BULK_DOWNLOAD_CSV_OMITTED_KEYS.detail)
        ]);

        this.messageService.add({
            severity: FcpMessageSeverity.Warning,
            summary,
            detail,
            life: 8000,
            closable: true
        });
    }

    /**
     * Warning toast when the CSV is downloading but has no row for some GTINs, listing why by reason.
     */
    async showCsvSkipped(reasons: BulkDownloadOmissionReason[]): Promise<void> {
        const [summary, detail] = await Promise.all([
            this.languageService.translate(BULK_DOWNLOAD_CSV_SKIPPED_KEYS.title),
            this.languageService.translate(BULK_DOWNLOAD_CSV_SKIPPED_KEYS.detail, {
                reasonList: formatOmissionReasonList(reasons)
            })
        ]);

        this.messageService.add({
            severity: FcpMessageSeverity.Warning,
            summary,
            detail,
            life: 8000,
            closable: true
        });
    }

    /**
     * Some identifiers matched, but none of the selected media types exist (empty main download URL).
     * Not shown when any selected type has assets (e.g. images found but no video).
     */
    async showNoAssetsFound(input: {
        searchBy: BulkDownloadToastSearchBy;
        selection: BulkDownloadToastSelection;
        downloadInformation: BulkDownloadImagesInformation;
    }): Promise<void> {
        const mediaList = await this.buildNoAssetsMediaList(input.selection);
        if (!mediaList) {
            return;
        }

        const identifierCount = Math.max(0, input.downloadInformation.requested - input.downloadInformation.notFound);
        const copyKeys = BULK_DOWNLOAD_NO_ASSETS_FOUND_KEYS[input.searchBy];

        let detail = await this.languageService.translate(copyKeys.detail, {
            count: identifierCount,
            mediaList
        });

        const omissionSuffix = await this.buildVideoOmissionSuffix(input.downloadInformation, {
            omissionReasons: BULK_DOWNLOAD_NO_ASSETS_FOUND_KEYS.omissionReasons
        });
        if (omissionSuffix) {
            detail = `${detail} ${omissionSuffix}`;
        }

        const summary = await this.languageService.translate(BULK_DOWNLOAD_NO_ASSETS_FOUND_KEYS.title);

        this.messageService.add({
            severity: FcpMessageSeverity.Error,
            summary,
            detail,
            life: 8000,
            closable: true
        });
    }

    /**
     * Success toast when the bulk request created a main download token (`downloadId` / non-empty download URL).
     */
    async showDownloadStarted(input: BulkDownloadStartedToastInput): Promise<void> {
        const detail = await this.buildDownloadStartedDetail(input);
        if (!detail) {
            return;
        }

        const summary = await this.languageService.translate(BULK_DOWNLOAD_STARTED_KEYS.title);
        const severity =
            input.selection.video && hasVideoOmissionFootnote(input.downloadInformation)
                ? FcpMessageSeverity.Warning
                : FcpMessageSeverity.Success;

        this.messageService.add({
            severity,
            summary,
            detail,
            life: 8000,
            closable: true
        });
    }

    /**
     * Toasts + optional navigation after bulk images / images+CSV API response.
     */
    async handleBulkDownloadResponse(
        result: BulkSearchResultWithInformation & {
            tooManyGtins?: boolean;
            bulkDownloadFailed?: boolean;
            bulkDownloadSearchBy?: BulkDownloadToastSearchBy;
            selection?: BulkDownloadToastSelection;
        },
        onStartDownload: () => void
    ): Promise<void> {
        if (result.tooManyGtins) {
            await this.showTooManyGtins(result.bulkDownloadSearchBy ?? 'gtins');
            return;
        }

        if (result.bulkDownloadFailed) {
            await this.showInternalError();
            return;
        }

        const downloadInformation = result.downloadInformation;
        if (downloadInformation && isBulkDownloadAllRequestedNotFound(downloadInformation)) {
            await this.showDownloadNotStarted({
                searchBy: bulkDownloadSearchByFromInformation(downloadInformation),
                downloadInformation
            });
            return;
        }

        if (
            _.isEmpty(result.downloadUrl) &&
            downloadInformation &&
            result.selection &&
            isBulkDownloadNoAssetsForSelectedOptions(result)
        ) {
            await this.showNoAssetsFound({
                searchBy: bulkDownloadSearchByFromInformation(downloadInformation),
                selection: result.selection,
                downloadInformation
            });
            return;
        }

        if (_.isEmpty(result.downloadUrl)) {
            return;
        }

        if (downloadInformation && result.selection) {
            await this.showDownloadStarted({
                searchBy: bulkDownloadSearchByFromInformation(downloadInformation),
                selection: result.selection,
                downloadInformation,
                additionalData: result.additionalData
            });
        }

        if (downloadInformation?.csvOmissionReasons?.length) {
            await this.showCsvSkipped(downloadInformation.csvOmissionReasons);
        }

        onStartDownload();
    }

    private async buildDownloadStartedDetail(input: BulkDownloadStartedToastInput): Promise<string> {
        const mediaList = await this.buildMediaList(input);
        if (!mediaList) {
            return '';
        }

        const { downloadInformation } = input;
        const identifierCount = Math.max(0, downloadInformation.requested - downloadInformation.notFound);
        const copyKeys = BULK_DOWNLOAD_STARTED_KEYS[input.searchBy];

        const sentences: string[] = [
            await this.languageService.translate(copyKeys.intro, {
                mediaList,
                count: identifierCount
            })
        ];

        if (downloadInformation.notFound > 0) {
            sentences.push(
                await this.languageService.translate(copyKeys.notFound, {
                    count: downloadInformation.notFound
                })
            );
        }

        if (input.selection.video && hasVideoOmissionFootnote(downloadInformation)) {
            const omissionSuffix = await this.buildVideoOmissionSuffix(downloadInformation, {
                videoOmitted: BULK_DOWNLOAD_STARTED_KEYS.videoOmitted,
                productsWithoutVideo: BULK_DOWNLOAD_STARTED_KEYS.productsWithoutVideo,
                omissionReasons: BULK_DOWNLOAD_STARTED_KEYS.videoOmissionReasons
            });

            if (omissionSuffix) {
                sentences.push(omissionSuffix);
            }
        }

        return sentences.join(' ');
    }

    private async buildMediaList(input: BulkDownloadStartedToastInput): Promise<string> {
        const { selection, additionalData } = input;
        const parts: string[] = [];

        if (selection.images) {
            parts.push(
                await this.languageService.translate(BULK_DOWNLOAD_STARTED_KEYS.mediaImages, {
                    count: additionalData?.allowedCount ?? 0
                })
            );
        }

        if (selection.video) {
            parts.push(
                await this.languageService.translate(BULK_DOWNLOAD_STARTED_KEYS.mediaVideos, {
                    count: additionalData?.videoCount ?? 0
                })
            );
        }

        if (selection.csv) {
            parts.push(await this.languageService.translate(BULK_DOWNLOAD_STARTED_KEYS.mediaCsv));
        }

        return this.joinMediaParts(BULK_DOWNLOAD_STARTED_KEYS, parts);
    }

    private async buildNoAssetsMediaList(selection: BulkDownloadToastSelection): Promise<string> {
        const parts: string[] = [];

        if (selection.images) {
            parts.push(await this.languageService.translate(BULK_DOWNLOAD_NO_ASSETS_FOUND_KEYS.mediaImages));
        }

        if (selection.video) {
            parts.push(await this.languageService.translate(BULK_DOWNLOAD_NO_ASSETS_FOUND_KEYS.mediaVideos));
        }

        if (selection.csv) {
            parts.push(await this.languageService.translate(BULK_DOWNLOAD_NO_ASSETS_FOUND_KEYS.mediaCsv));
        }

        return this.joinMediaParts(BULK_DOWNLOAD_NO_ASSETS_FOUND_KEYS, parts);
    }

    private async joinMediaParts(keys: MediaListTranslationKeys, parts: string[]): Promise<string> {
        if (parts.length === 0) {
            return '';
        }

        if (parts.length === 1) {
            return parts[0];
        }

        if (parts.length === 2) {
            return this.languageService.translate(keys.mediaPair, {
                first: parts[0],
                second: parts[1]
            });
        }

        const conjunction = await this.languageService.translate(keys.mediaConjunction);

        return `${parts.slice(0, -1).join(', ')}, ${conjunction} ${parts[parts.length - 1]}`;
    }

    private async buildVideoOmissionSuffix(
        downloadInformation: BulkDownloadImagesInformation,
        phraseKeys: {
            videoOmitted?: string;
            productsWithoutVideo?: string;
            omissionReasons: string;
        }
    ): Promise<string> {
        const parts: string[] = [];

        if (phraseKeys.videoOmitted && downloadInformation.videoOmitted) {
            parts.push(await this.languageService.translate(phraseKeys.videoOmitted));
        }

        const productsWithoutVideo = downloadInformation.productsWithoutVideo ?? 0;
        if (phraseKeys.productsWithoutVideo && productsWithoutVideo > 0) {
            parts.push(
                await this.languageService.translate(phraseKeys.productsWithoutVideo, {
                    count: productsWithoutVideo
                })
            );
        }

        const reasons = downloadInformation.videoOmissionReasons;
        if (reasons?.length) {
            parts.push(
                await this.languageService.translate(phraseKeys.omissionReasons, {
                    reasonList: formatOmissionReasonList(reasons)
                })
            );
        }

        return parts.join(' ');
    }
}

/** `reason1 (count1); reason2 (count2)`, the list the omission toasts show. */
function formatOmissionReasonList(reasons: BulkDownloadOmissionReason[]): string {
    return reasons.map(({ reason, gtinCount }) => `${reason} (${gtinCount})`).join('; ');
}

export function bulkDownloadSearchByFromInformation(
    downloadInformation: BulkDownloadImagesInformation
): BulkDownloadToastSearchBy {
    return downloadInformation.searchBy === 'articleNumbers' ? 'articleNumbers' : 'gtins';
}

/** PCS / catalog video footnote on an otherwise successful bulk download. */
export function hasVideoOmissionFootnote(downloadInformation: BulkDownloadImagesInformation): boolean {
    if (downloadInformation.videoOmitted) {
        return true;
    }

    if ((downloadInformation.productsWithoutVideo ?? 0) > 0) {
        return true;
    }

    return (downloadInformation.videoOmissionReasons?.length ?? 0) > 0;
}

/** Every requested identifier was not found (`requested === notFound`). Uses `searchBy` for copy only. */
export function isBulkDownloadAllRequestedNotFound(downloadInformation: BulkDownloadImagesInformation): boolean {
    const requested = Number(downloadInformation.requested);
    const notFound = Number(downloadInformation.notFound);

    return Number.isFinite(requested) && Number.isFinite(notFound) && requested > 0 && requested === notFound;
}

export function isBulkDownloadTooManyGtinsError(error: unknown): boolean {
    const actualError = typeof error === 'function' ? (error as () => { data?: { type?: string } })() : error;
    return (actualError as { data?: { type?: string } })?.data?.type === 'TooManyGtinsError';
}

/** With the unified CSV flag off, a CSV-only download returns no information when nothing is downloadable — attach it for the not-started toast. */
export function withBulkDownloadCsvEmptyResponseInformation<
    T extends { downloadUrl: string; tooManyGtins?: boolean; downloadInformation?: BulkDownloadImagesInformation }
>(
    result: T,
    searchBy: BulkDownloadToastSearchBy,
    requestedCount: number
): T & {
    bulkDownloadSearchBy: BulkDownloadToastSearchBy;
    downloadInformation?: BulkDownloadImagesInformation;
} {
    const bulkDownloadSearchBy = searchBy;

    if (result.tooManyGtins || result.downloadInformation || !_.isEmpty(result.downloadUrl) || requestedCount <= 0) {
        return { ...result, bulkDownloadSearchBy };
    }

    return {
        ...result,
        bulkDownloadSearchBy,
        downloadInformation: {
            searchBy: searchBy === 'articleNumbers' ? 'articleNumbers' : 'gtins',
            requested: requestedCount,
            notFound: requestedCount,
            notFoundImages: 0
        }
    };
}

/** True when at least one selected media type has deliverable assets in `additionalData`. */
export function hasAssetsForAnySelectedOption(
    selection: BulkDownloadToastSelection,
    additionalData?: BulkDownloadImagesStatistics
): boolean {
    if (selection.images && (additionalData?.allowedCount ?? 0) > 0) {
        return true;
    }

    if (selection.video && (additionalData?.videoCount ?? 0) > 0) {
        return true;
    }

    // CSV deliverable count: wire when bulk CSV stats are exposed on the response.
    return false;
}

/**
 * Empty main download URL, some IDs found, and every selected media type has zero assets.
 * Excludes partial cases (e.g. images found + video selected but no videos → no toast).
 */
export function isBulkDownloadNoAssetsForSelectedOptions(
    result: BulkSearchResultWithInformation & { selection?: BulkDownloadToastSelection }
): boolean {
    if (!_.isEmpty(result.downloadUrl)) {
        return false;
    }

    const downloadInformation = result.downloadInformation;
    if (!downloadInformation || isBulkDownloadAllRequestedNotFound(downloadInformation)) {
        return false;
    }

    const foundCount = Number(downloadInformation.requested) - Number(downloadInformation.notFound);
    if (!Number.isFinite(foundCount) || foundCount <= 0) {
        return false;
    }

    const selection = result.selection;
    if (!selection || (!selection.images && !selection.video && !selection.csv)) {
        return false;
    }

    return !hasAssetsForAnySelectedOption(selection, result.additionalData);
}
